import {
  CardStyleInterpolators,
  StackScreenProps,
} from "@react-navigation/stack";
import {
  createStackHeaderNavigator,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import { CancelSaleScreen } from "../CancelSaleScreen";
import { DiscountSaleScreen } from "../DiscountSaleScreen";
import { EditSaleNoteScreen } from "../EditSaleNoteScreen";
import { MoreActionsScreen } from "../MoreActionsScreen";
import { ParkScreen } from "../ParkScreen";
import { SwitchSaleScreen } from "../SwitchSaleScreen";
import { ModalCloseButton } from "@towersystems/roam-common-native";
import { PartialConfirmTransferWebSaleScreen } from "../PartialConfirmTransferWebSaleScreen";
import { TransferSaleRetailerScreen } from "../TransferSaleRetailerScreen";
import { CustomerRootScreen } from "../CustomerRootScreen";
import { ActionsRootParamList, RegisterRootParamList } from "../../../../types";
import { CustomerRootScreenActionsContext } from "../CustomerRootScreen/context";
import React from "react";
import { useSaleControllerProvider } from "../../utils/sale-controller";
import { ActionCreateQuoteSaleRootScreen } from "../ActionCreateQuoteSaleRootScreen";
import { ActionCreateInvoiceSaleRootScreen } from "../ActionCreateInvoiceSaleRootScreen";
import { EditPurchaseOrderNumberScreen } from "../EditPurchaseOrderNumberScreen";
import { AddGiftVoucherRootScreen } from "../AddGiftVoucherScreen";
import { DynamicMobileText } from "../../../../components/DynamicMobileText";
import { ActionCreateProformaSaleRootScreen } from "../ActionCreateProformaSaleRootScreen/ActionCreateProformaSaleRootScreen";
import { AddNonMatchedProductScreen } from "../AddNonMatchedProductScreen";
import { vendorCanAccessFeature } from "../../../../utilities/feature-flags";
import { AccountFeatureFlag } from "@towersystems/roam-common/lib/generated-types";
import { useCurrentVendorProvider } from "../../../../utilities/vendor";

export interface ActionsRootScreenProps
  extends StackScreenProps<RegisterRootParamList, "ActionsRootScreen"> {}

export const ActionsRootScreen = ({ navigation }: ActionsRootScreenProps) => {
  const { sale } = useSaleControllerProvider();
  const { showMessage } = useFlashMessages();
  const { currentVendor } = useCurrentVendorProvider();
  const RootNavigator = createStackHeaderNavigator<ActionsRootParamList>();

  const handleOnCustomerSelected = React.useCallback(
    async (customerId: string) => {
      if (sale) {
        try {
          await sale.addCustomer(customerId);
          navigation.pop();
        } catch (err) {
          showMessage({
            message: "Error adding customer to sale",
            severity: "error",
          });
        }
      }
    },
    [sale]
  );

  return (
    <CustomerRootScreenActionsContext.Provider
      value={{
        onCustomerSelected: handleOnCustomerSelected,
      }}
    >
      <RootNavigator.Navigator>
        <RootNavigator.Group
          screenOptions={{
            animationEnabled: true,
            cardStyleInterpolator: CardStyleInterpolators.forHorizontalIOS,
          }}
        >
          <RootNavigator.Screen
            options={{
              headerTitle: () => <DynamicMobileText label="More Actions" />,
            }}
            name="MoreActionsScreen"
            component={MoreActionsScreen}
          />
          <RootNavigator.Group
            screenOptions={{
              headerLeft: () => <ModalCloseButton type="Back" />,
              title: "",
            }}
          >
            <RootNavigator.Screen
              options={{
                headerTitle: () => <DynamicMobileText label="Switch Sale" />,
              }}
              name="SwitchSaleScreen"
              component={SwitchSaleScreen}
            />
            <RootNavigator.Screen
              name="EditSaleNoteScreen"
              component={EditSaleNoteScreen}
            />

            <RootNavigator.Screen
              options={{ title: "Customers", headerShown: false }}
              name="CustomerRootScreen"
              component={CustomerRootScreen}
            />

            <RootNavigator.Screen
              name="DiscountSaleScreen"
              component={DiscountSaleScreen}
            />
            <RootNavigator.Screen
              name="CancelSaleScreen"
              component={CancelSaleScreen}
              options={{
                headerTitle: () => <DynamicMobileText label="Cancel Sale" />,
              }}
            />
            <RootNavigator.Screen
              name="ParkScreen"
              component={ParkScreen}
              options={{
                headerTitle: () => <DynamicMobileText label="Park Sale" />,
              }}
            />

            {vendorCanAccessFeature(
              AccountFeatureFlag.POS_TRANSFER_RETAILER_UI,
              currentVendor
            ) && (
              <RootNavigator.Screen
                name="TransferSaleRetailerScreen"
                component={TransferSaleRetailerScreen}
                options={{ title: "Transfer Sale to Retailer" }}
              />
            )}

            <RootNavigator.Screen
              name="EditPurchaseOrderNumberScreen"
              component={EditPurchaseOrderNumberScreen}
            />
            <RootNavigator.Screen
              name="AddNonMatchedProductScreen"
              component={AddNonMatchedProductScreen}
            />
          </RootNavigator.Group>
        </RootNavigator.Group>
        <RootNavigator.Group
          screenOptions={{
            headerShown: false,
            animationEnabled: true,
            cardStyleInterpolator: CardStyleInterpolators.forHorizontalIOS,
          }}
        >
          {vendorCanAccessFeature(
            AccountFeatureFlag.POS_GIFT_VOUCHER_UI,
            currentVendor
          ) && (
            <RootNavigator.Screen
              name="AddGiftVoucherRootScreen"
              component={AddGiftVoucherRootScreen}
            />
          )}
          {vendorCanAccessFeature(
            AccountFeatureFlag.POS_APP_QUOTES_UI,
            currentVendor
          ) && (
            <RootNavigator.Screen
              name="ActionCreateQuoteSaleRootScreen"
              component={ActionCreateQuoteSaleRootScreen}
            />
          )}

          {vendorCanAccessFeature(
            AccountFeatureFlag.POS_APP_INVOICES_UI,
            currentVendor
          ) && (
            <RootNavigator.Screen
              name="ActionCreateInvoiceSaleRootScreen"
              component={ActionCreateInvoiceSaleRootScreen}
            />
          )}

          {vendorCanAccessFeature(
            AccountFeatureFlag.POS_APP_PROFORMA_SALES_UI,
            currentVendor
          ) && (
            <RootNavigator.Screen
              name="ActionCreateProformaSaleRootScreen"
              component={ActionCreateProformaSaleRootScreen}
            />
          )}
        </RootNavigator.Group>
        <RootNavigator.Group
          screenOptions={{
            presentation: "transparentModal",
            headerShown: false,
            animationEnabled: true,
            cardStyle: {
              backgroundColor: "transparent",
            },
          }}
        >
          <RootNavigator.Screen
            name="PartialConfirmTransferWebSaleScreen"
            component={PartialConfirmTransferWebSaleScreen}
          />
        </RootNavigator.Group>
      </RootNavigator.Navigator>
    </CustomerRootScreenActionsContext.Provider>
  );
};
